import React from 'react';
import { Search, Filter } from 'lucide-react';

export interface ClientesFilters {
  searchTerm: string;
  diaVisita: string;
  frecuenciaVisita: string;
  mostrarDeudores: boolean;
  mostrarEntregasRecientes: boolean;
}

interface ClientesFiltersProps {
  filters: ClientesFilters;
  onFiltersChange: (filters: ClientesFilters) => void;
  className?: string;
}

export const ClientesFilters: React.FC<ClientesFiltersProps> = ({
  filters,
  onFiltersChange,
  className = ''
}) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    onFiltersChange({
      ...filters,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    onFiltersChange({
      ...filters,
      [name]: checked,
    });
  };

  return (
    <div className={className}>
      {/* Layout optimizado para PC - Header de filtros */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Filter className="h-5 w-5 text-blue-600 mr-2" />
          Filtros de Búsqueda
        </h3>
        <button
          onClick={() => onFiltersChange({
            searchTerm: '',
            diaVisita: '',
            frecuenciaVisita: '',
            mostrarDeudores: false,
            mostrarEntregasRecientes: false,
          })}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
        >
          Limpiar filtros
        </button>
      </div>

      {/* Filtros organizados en dos filas */}
      <div className="space-y-4">
        {/* Primera fila: Búsqueda principal + Botón desplegable */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleInputChange}
              placeholder="Buscar por nombre, dirección, teléfono..."
              className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
              showAdvancedFilters 
                ? 'bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/20 dark:border-blue-400 dark:text-blue-400' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <Filter className="h-4 w-4 mr-1.5" />
            Filtros
          </button>
        </div>

        {/* Segunda fila colapsable: Filtros específicos */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 animate-slideDown">
            {/* Filtro por día de visita */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Día de Visita
              </label>
              <select
                name="diaVisita"
                value={filters.diaVisita}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="">Todos los días</option>
                <option value="lunes">Lunes</option>
                <option value="martes">Martes</option>
                <option value="miércoles">Miércoles</option>
                <option value="jueves">Jueves</option>
                <option value="viernes">Viernes</option>
                <option value="sábado">Sábado</option>
                <option value="domingo">Domingo</option>
              </select>
            </div>

            {/* Filtro por frecuencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Frecuencia
              </label>
              <select
                name="frecuenciaVisita"
                value={filters.frecuenciaVisita}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="">Todas las frecuencias</option>
                <option value="semanal">Semanal</option>
                <option value="quincenal">Quincenal</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>

            {/* Filtros rápidos */}
            <div className="flex flex-col space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Filtros Rápidos
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="mostrarDeudores"
                    checked={filters.mostrarDeudores}
                    onChange={handleCheckboxChange}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:checked:bg-red-600 dark:checked:border-red-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Con deuda</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="mostrarEntregasRecientes"
                    checked={filters.mostrarEntregasRecientes}
                    onChange={handleCheckboxChange}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600 dark:checked:bg-green-600 dark:checked:border-green-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Entregas recientes</span>
                </label>
              </div>
            </div>

            {/* Resumen de filtros activos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Filtros Activos
              </label>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {[
                  filters.searchTerm && 'Búsqueda',
                  filters.diaVisita && 'Día',
                  filters.frecuenciaVisita && 'Frecuencia',
                  filters.mostrarDeudores && 'Deudores',
                  filters.mostrarEntregasRecientes && 'Recientes'
                ].filter(Boolean).length || 0} filtro(s) aplicado(s)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 